// src/components/setting/ProductManager.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, update, set, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { contentUsersDatabase } from '../../firebase';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import SortableProductItem from './SortableProductItem';
import shared from '../../styles/shared.module.css';
import styles from './ProductManager.module.css';

const ProductManager = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sortMode, setSortMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // 新規商品と編集中の商品のフォームデータ
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    description: '',
    type: '未分類',
    isHidden: false,
    image: null,
    ingredients: {
      main: { name: '', amount: '' },
      secondary: { name: '', amount: '' }
    }
  });

  const fileInputRef = useRef(null);
  const popupRef = useRef(null);

  // ドラッグアンドドロップのセンサー設定
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 管理者権限チェック
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser) {
        try {
          const adminStatus = await isAdmin(currentUser.uid);
          setIsAdminUser(adminStatus);

          if (!adminStatus) {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('管理者権限チェックエラー:', error);
          navigate('/dashboard');
        }
      }
    };

    checkAdmin();
  }, [currentUser, isAdmin, navigate]);

  // 商品データを取得
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const db = contentUsersDatabase;
        const productsRef = ref(db, 'products/items');

        onValue(productsRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const productsArray = Object.keys(data).map(key => ({
              id: key,
              ...data[key],
              image: data[key].imageUrl || data[key].image,
              isHidden: data[key].status === 'hidden' || data[key].isHidden === true,
              type: data[key].category || data[key].type || '未分類',
              sortOrder: data[key].sortOrder !== undefined ? data[key].sortOrder : 999
            }));

            // sortOrderでソート（未設定のものは最後に）
            const sortedProducts = productsArray.sort((a, b) => {
              if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
              }
              // sortOrderが同じ場合はIDでソート
              return a.id?.localeCompare(b.id);
            });

            setProducts(sortedProducts);
          } else {
            setProducts([]);
          }
          setLoading(false);
        }, (error) => {
          console.error('商品データの取得エラー:', error);
          setError('商品データの取得に失敗しました。' + error.message);
          setLoading(false);
        });
      } catch (error) {
        console.error('商品データの取得エラー:', error);
        setError('商品データの取得に失敗しました。');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // ドラッグ終了時の処理
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setProducts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // 並び替え順序を保存
  const handleSaveSortOrder = async () => {
    if (!isAdminUser) {
      setError('この操作を行うには管理者権限が必要です。');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const db = contentUsersDatabase;
      const updatePromises = products.map((product, index) => {
        const productRef = ref(db, `products/items/${product.id}`);
        return update(productRef, {
          sortOrder: index,
          updatedAt: new Date().toISOString()
        });
      });

      await Promise.all(updatePromises);
      setSuccess('商品の並び順を保存しました。');
      setSortMode(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('並び順保存エラー:', error);
      setError(`並び順の保存に失敗しました: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 並び替えモードの切り替え
  const toggleSortMode = () => {
    setSortMode(!sortMode);
    if (sortMode) {
      // キャンセル時は元の状態に戻す
      window.location.reload();
    }
  };

  // ポップアップ外のクリックを検知
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClosePopup();
      }
    };

    if (showEditPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditPopup]);

  // 商品を選択して編集モードに入る
  const handleSelectProduct = (product) => {
    if (sortMode) return; // 並び替えモード中は編集不可

    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      price: product.price || 0,
      description: product.description || '',
      type: product.type || '未分類',
      isHidden: product.isHidden || false,
      image: null,
      currentImageURL: product.image || '',
      ingredients: product.ingredients || {
        main: { name: '', amount: '' },
        secondary: { name: '', amount: '' }
      }
    });
    setShowEditPopup(true);
    setEditMode(true);
    setShowAddForm(false);
    setConfirmDelete(false);
  };

  // ポップアップを閉じる
  const handleClosePopup = () => {
    setShowEditPopup(false);
    setTimeout(() => {
      setSelectedProduct(null);
      setEditMode(false);
      resetForm();
      setConfirmDelete(false);
    }, 300);
  };

  // 新規商品追加フォームを表示
  const handleShowAddForm = () => {
    setShowAddForm(true);
    setEditMode(false);
    setSelectedProduct(null);
    resetForm();
  };

  // 新規商品追加フォームをキャンセル
  const handleCancelAddForm = () => {
    setShowAddForm(false);
    resetForm();
  };

  // フォームをリセット
  const resetForm = () => {
    setFormData({
      name: '',
      price: 0,
      description: '',
      type: '未分類',
      isHidden: false,
      image: null,
      ingredients: {
        main: { name: '', amount: '' },
        secondary: { name: '', amount: '' }
      }
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 商品の表示/非表示を切り替え
  const handleToggleVisibility = async (product, e) => {
    e.stopPropagation();

    try {
      const db = contentUsersDatabase;
      const productRef = ref(db, `products/items/${product.id}`);

      const updateData = {
        status: product.isHidden ? 'active' : 'hidden',
        updatedAt: new Date().toISOString()
      };

      await update(productRef, updateData);
    } catch (error) {
      console.error('商品表示状態の変更エラー:', error);
      setError(`商品表示状態の変更に失敗しました: ${error.message}`);
    }
  };

  // 削除確認を表示
  const handleShowDeleteConfirm = () => {
    setConfirmDelete(true);
  };

  // 商品を削除
  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      const db = contentUsersDatabase;
      const productRef = ref(db, `products/items/${selectedProduct.id}`);

      if (selectedProduct.image && selectedProduct.image.includes('firebasestorage')) {
        try {
          const storage = getStorage();
          const urlParts = selectedProduct.image.split('/');
          const imageName = urlParts[urlParts.length - 1].split('?')[0];
          const imageRef = storageRef(storage, `products/${imageName}`);

          await deleteObject(imageRef);
          console.log(`商品画像を削除しました: ${imageName}`);
        } catch (imageError) {
          console.warn('画像の削除に失敗しましたが、商品削除を続行します:', imageError);
        }
      }

      await remove(productRef);

      setSuccess(`商品「${selectedProduct.name}」を削除しました`);
      handleClosePopup();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('商品削除エラー:', error);
      setError(`商品削除に失敗しました: ${error.message}`);
    }
  };

  // フォーム入力の変更を処理
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'price') {
      const numValue = value === '' ? 0 : parseInt(value, 10);
      setFormData(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? 0 : numValue
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name.startsWith('ingredients.')) {
      const parts = name.split('.');
      const ingredientType = parts[1];
      const property = parts[2];

      setFormData(prev => ({
        ...prev,
        ingredients: {
          ...prev.ingredients,
          [ingredientType]: {
            ...prev.ingredients[ingredientType],
            [property]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 画像ファイル選択時の処理
  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData(prev => ({
        ...prev,
        image: e.target.files[0]
      }));
    }
  };

  // 画像ファイルをアップロード
  const uploadImage = async (file) => {
    if (!file) return null;

    const storage = getStorage();
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const imageRef = storageRef(storage, `products/${fileName}`);

    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);

    return downloadURL;
  };

  // 新しい商品IDを生成
  const generateNextProductId = () => {
    const productIds = products
      .map(p => p.id)
      .filter(id => id && id.startsWith('product'))
      .map(id => {
        const numPart = id.replace('product', '');
        return parseInt(numPart, 10);
      })
      .filter(num => !isNaN(num));

    if (productIds.length === 0) {
      return 'product1';
    }

    const maxProductNum = Math.max(...productIds);
    return `product${maxProductNum + 1}`;
  };

  // 商品を保存（新規または更新）
  const handleSaveProduct = async (e) => {
    e.preventDefault();

    if (!isAdminUser) {
      setError('この操作を行うには管理者権限が必要です。');
      return;
    }

    if (!formData.name.trim()) {
      setError('商品名を入力してください。');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const db = contentUsersDatabase;
      let imageURL = formData.currentImageURL || '';

      if (formData.image) {
        imageURL = await uploadImage(formData.image);
      }

      const productData = {
        name: formData.name.trim(),
        price: formData.price,
        description: formData.description.trim(),
        category: formData.type.trim() || '未分類',
        status: formData.isHidden ? 'hidden' : 'active',
        imageUrl: imageURL,
        ingredients: formData.ingredients,
        updatedAt: new Date().toISOString()
      };

      if (editMode && selectedProduct) {
        // 既存商品の更新（sortOrderは保持）
        productData.sortOrder = selectedProduct.sortOrder;
        const productRef = ref(db, `products/items/${selectedProduct.id}`);
        await update(productRef, productData);
        setSuccess(`商品「${productData.name}」を更新しました`);
      } else {
        // 新規商品の追加（最後の位置に追加）
        const newProductId = generateNextProductId();
        const newSortOrder = products.length > 0 ? Math.max(...products.map(p => p.sortOrder || 0)) + 1 : 0;
        productData.sortOrder = newSortOrder;
        const productRef = ref(db, `products/items/${newProductId}`);

        await set(productRef, {
          ...productData,
          createdAt: new Date().toISOString()
        });
        setSuccess(`商品「${productData.name}」を追加しました (ID: ${newProductId})`);
      }

      handleClosePopup();
      setShowAddForm(false);

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('商品保存エラー:', error);
      setError(`商品の保存に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 商品タイプの一意な一覧を取得
  const uniqueProductTypes = [...new Set(products.map(p => p.type || '未分類'))].sort();

  if (loading && products.length === 0) {
    return (
      <div className={shared.loadingSpinnerShort}>
        <div className={shared.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.productManagerContainer}>
      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={() => setError('')} className={styles.closeButton}>×</button>
        </div>
      )}

      {success && (
        <div className={styles.successMessage}>
          {success}
          <button onClick={() => setSuccess('')} className={styles.closeButton}>×</button>
        </div>
      )}

      <div className={styles.controlPanel}>
        <div className={styles.controlButtons}>
          <button
            onClick={toggleSortMode}
            disabled={editMode || showAddForm || saving}
            className={`${styles.sortButton} ${sortMode ? styles.sortButtonActive : ''}`}
          >
            {sortMode ? '並び替えをキャンセル' : '商品を並び替え'}
          </button>

          {sortMode && (
            <button
              onClick={handleSaveSortOrder}
              disabled={saving}
              className={styles.saveSortButton}
            >
              {saving ? '保存中...' : '並び順を保存'}
            </button>
          )}

          <button
            onClick={handleShowAddForm}
            disabled={editMode || showAddForm || sortMode}
            className={styles.addButton}
          >
            新規商品を追加
          </button>
        </div>
      </div>

      {sortMode && (
        <div className={styles.sortModeInfo}>
          <p>商品をドラッグして並び替えてください。この順序がメニューに反映されます。</p>
        </div>
      )}

      {/* 新規商品追加フォーム */}
      {showAddForm && (
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>新規商品の追加</h3>
          <form onSubmit={handleSaveProduct}>
            <div className={styles.formGroup}>
              <label className={styles.label}>商品名 <span className={styles.required}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>価格 (円)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleFormChange}
                  min="0"
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>カテゴリ</label>
                <input
                  type="text"
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  className={styles.input}
                  list="product-types"
                />
                <datalist id="product-types">
                  {uniqueProductTypes.map(type => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>商品説明</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                className={styles.textarea}
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>材料情報</label>
              <div className={styles.ingredientsContainer}>
                <div className={styles.ingredientRow}>
                  <div className={styles.ingredientCol}>
                    <label className={styles.smallLabel}>メイン材料</label>
                    <input
                      type="text"
                      name="ingredients.main.name"
                      value={formData.ingredients.main.name}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="材料名"
                    />
                  </div>
                  <div className={styles.ingredientCol}>
                    <label className={styles.smallLabel}>量</label>
                    <input
                      type="text"
                      name="ingredients.main.amount"
                      value={formData.ingredients.main.amount}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="45ml"
                    />
                  </div>
                </div>

                <div className={styles.ingredientRow}>
                  <div className={styles.ingredientCol}>
                    <label className={styles.smallLabel}>サブ材料</label>
                    <input
                      type="text"
                      name="ingredients.secondary.name"
                      value={formData.ingredients.secondary.name}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="材料名"
                    />
                  </div>
                  <div className={styles.ingredientCol}>
                    <label className={styles.smallLabel}>量</label>
                    <input
                      type="text"
                      name="ingredients.secondary.amount"
                      value={formData.ingredients.secondary.amount}
                      onChange={handleFormChange}
                      className={styles.input}
                      placeholder="15ml"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>商品画像</label>
              <input
                type="file"
                onChange={handleImageChange}
                className={styles.fileInput}
                accept="image/*"
                ref={fileInputRef}
              />
              <p className={styles.helperText}>画像がない場合はプレースホルダーが使用されます</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isHidden"
                  checked={formData.isHidden}
                  onChange={handleFormChange}
                  className={styles.checkbox}
                />
                メニューに表示しない
              </label>
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={loading}
              >
                {loading ? '保存中...' : '商品を追加'}
              </button>
              <button
                type="button"
                onClick={handleCancelAddForm}
                className={styles.cancelButton}
                disabled={loading}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 編集ポップアップ */}
      {showEditPopup && selectedProduct && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContent}
            ref={popupRef}
          >
            <button
              className={styles.modalCloseButton}
              onClick={handleClosePopup}
            >
              ×
            </button>

            <h3 className={styles.modalTitle}>商品の編集: {selectedProduct.name}</h3>

            {confirmDelete ? (
              <div className={styles.deleteConfirm}>
                <p className={styles.deleteConfirmText}>
                  本当に商品「{selectedProduct.name}」を削除しますか？<br />
                  この操作は元に戻せません。
                </p>
                <div className={styles.deleteConfirmButtons}>
                  <button
                    className={styles.deleteConfirmButton}
                    onClick={handleDeleteProduct}
                  >
                    削除する
                  </button>
                  <button
                    className={styles.cancelDeleteButton}
                    onClick={() => setConfirmDelete(false)}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSaveProduct}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>商品名 <span className={styles.required}>*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>価格 (円)</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleFormChange}
                      min="0"
                      className={styles.input}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>カテゴリ</label>
                    <input
                      type="text"
                      name="type"
                      value={formData.type}
                      onChange={handleFormChange}
                      className={styles.input}
                      list="edit-product-types"
                    />
                    <datalist id="edit-product-types">
                      {uniqueProductTypes.map(type => (
                        <option key={type} value={type} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>商品説明</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className={styles.textarea}
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>材料情報</label>
                  <div className={styles.ingredientsContainer}>
                    <div className={styles.ingredientRow}>
                      <div className={styles.ingredientCol}>
                        <label className={styles.smallLabel}>メイン材料</label>
                        <input
                          type="text"
                          name="ingredients.main.name"
                          value={formData.ingredients.main.name}
                          onChange={handleFormChange}
                          className={styles.input}
                          placeholder="材料名"
                        />
                      </div>
                      <div className={styles.ingredientCol}>
                        <label className={styles.smallLabel}>量</label>
                        <input
                          type="text"
                          name="ingredients.main.amount"
                          value={formData.ingredients.main.amount}
                          onChange={handleFormChange}
                          className={styles.input}
                          placeholder="45ml"
                        />
                      </div>
                    </div>

                    <div className={styles.ingredientRow}>
                      <div className={styles.ingredientCol}>
                        <label className={styles.smallLabel}>サブ材料</label>
                        <input
                          type="text"
                          name="ingredients.secondary.name"
                          value={formData.ingredients.secondary.name}
                          onChange={handleFormChange}
                          className={styles.input}
                          placeholder="材料名"
                        />
                      </div>
                      <div className={styles.ingredientCol}>
                        <label className={styles.smallLabel}>量</label>
                        <input
                          type="text"
                          name="ingredients.secondary.amount"
                          value={formData.ingredients.secondary.amount}
                          onChange={handleFormChange}
                          className={styles.input}
                          placeholder="15ml"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>現在の画像</label>
                  {formData.currentImageURL ? (
                    <div className={styles.currentImageContainer}>
                      <img
                        src={formData.currentImageURL}
                        alt={formData.name}
                        className={styles.currentImage}
                      />
                    </div>
                  ) : (
                    <p className={styles.noImageText}>画像がありません</p>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>新しい画像 (変更する場合のみ)</label>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    className={styles.fileInput}
                    accept="image/*"
                    ref={fileInputRef}
                  />
                  <p className={styles.helperText}>画像を変更しない場合は現在の画像が使用されます</p>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isHidden"
                      checked={formData.isHidden}
                      onChange={handleFormChange}
                      className={styles.checkbox}
                    />
                    メニューに表示しない
                  </label>
                </div>

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    className={styles.saveButton}
                    disabled={loading}
                  >
                    {loading ? '保存中...' : '変更を保存'}
                  </button>
                  <button
                    type="button"
                    onClick={handleShowDeleteConfirm}
                    className={styles.deleteButtonInPopup}
                  >
                    この商品を削除
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* 商品一覧表示 */}
      {!loading && products.length > 0 && !showAddForm && (
        <div className={styles.productListContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.colName}>商品名</div>
            <div className={styles.colType}>カテゴリ</div>
            <div className={styles.colActions}>操作</div>
          </div>

          {sortMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={products.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {products.map(product => (
                  <SortableProductItem
                    key={product.id}
                    product={product}
                    onToggleVisibility={handleToggleVisibility}
                    sortMode={sortMode}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            products.map(product => (
              <div
                key={product.id}
                className={`${styles.productRow} ${product.isHidden ? styles.productRowHidden : ''}`}
                onClick={() => handleSelectProduct(product)}
              >
                <div className={styles.colName}>
                  <div className={styles.productInfo}>
                    <div className={styles.productImageThumb}>
                      <img
                        src={product.image || "/api/placeholder/60/60"}
                        alt={product.name}
                        className={styles.thumbImage}
                      />
                    </div>
                    <div className={styles.productNameWrapper}>
                      <span className={styles.productName}>{product.name}</span>
                      {product.description && (
                        <span className={styles.productDesc}>
                          {product.description.length > 30
                            ? `${product.description.substring(0, 30)}...`
                            : product.description}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.colType}>
                  {product.type || '未分類'}
                </div>
                <div className={styles.colActions}>
                  <div className={styles.actionButtons}>
                    <button
                      onClick={(e) => handleToggleVisibility(product, e)}
                      className={product.isHidden ? styles.showButton : styles.hideButton}
                      title={product.isHidden ? '表示する' : '非表示にする'}
                    >
                      {product.isHidden ? '👁' : '🚫'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {!loading && products.length === 0 && !showAddForm && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>
            商品が登録されていません。「新規商品を追加」ボタンをクリックして商品を登録してください。
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductManager;